import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import * as S from './PaintPicker.style';
import { AngleLeft } from '@archipro-design/icons';
import { motion } from 'framer-motion';
import {
    Image,
    useAppDisplayModeContext,
    useStyles,
} from '@archipro-design/aria';
import { PaintPalette } from '@modules/paint-picker-new/component/paint-colours/PaintPalette';
import {
    RESENE_LOGO,
    RESENE_PROFESSIONAL_LINK,
} from '@modules/paint-picker-new/config';
import { usePaintPickerContext } from '@modules/paint-picker-new/context/PaintPickerContext';
import { useConfigContext } from '@archipro-website/config/bindings/react';
import { useTracker, useImpression } from '@archipro-website/tracker';
import { PaintPickerSavePaletteEmptyModal } from '@modules/paint-picker-new/component/paint-picker/PaintPickerSavePaletteEmptyModal';
import { useHandleFollow } from '@modules-rocco/design-board/hook/use-handle-follow';
interface PaintPickerProps {
    bannerUrl?: string;
}

export const PaintPicker = ({ bannerUrl }: PaintPickerProps) => {
    const { desktop } = useAppDisplayModeContext();
    const config = useConfigContext();
    const {
        containerRef,
        state: { isEnabled, paintPickerActive, isSavingPalette },
        dispatch,
        closePaintPicker,
        paintSelection,
        openPaintPicker,
        savePaletteToDesignBoard,
    } = usePaintPickerContext();

    const [isAnimating, setIsAnimating] = useState(false);
    const [showEmptyPaletteError, setShowEmptyPaletteError] = useState(false);

    const { css } = useStyles({ desktop, paintPickerActive });
    const { handleFollow } = useHandleFollow();

    const pickerContainerRef = useRef<HTMLDivElement>(null);
    const paintsContainerRef = useRef<HTMLUListElement>(null);
    const reseneAdRef = useRef(null);

    const tracker = useTracker();

    useImpression({
        ref: pickerContainerRef,
        onImpression: (url: URL) =>
            tracker.log('ReseneToolViewedImpression', { url }),
    });

    const openPaintPalette = () => {
        if (!isAnimating) {
            openPaintPicker();
        }
    };

    const closePaintPalette = () => {
        if (!isAnimating) {
            closePaintPicker();
        }
    };

    const setBanner = useCallback(() => {
        if (desktop && paintSelection.length === 0 && bannerUrl) {
            // set banner image as active image to draw random colours from initially
            dispatch({ type: 'SET_ACTIVE_IMAGE', payload: bannerUrl });
        }
    }, [desktop, bannerUrl, dispatch, paintSelection.length]);

    const { drawerRef, drawerPosition, dragHandlers } =
        usePaintDrawerMobileDrag(closePaintPalette);

    const additionalContainerProps = !desktop
        ? {
              ...dragHandlers,
              style: { bottom: -drawerPosition },
          }
        : {};

    // we need to call it to be able to set random paints on first load
    useLayoutEffect(() => setBanner(), [setBanner]);

    // Reset scroll variables when picker state changes
    const scrollVariablesRef = useRef({
        lastScrollY: window.scrollY,
        scrollDistance: 0,
    });
    useEffect(() => {
        // Reset scroll tracking when picker is opened
        if (paintPickerActive) {
            scrollVariablesRef.current = {
                lastScrollY: window.scrollY,
                scrollDistance: 0,
            };
        }
    }, [paintPickerActive]);

    useLayoutEffect(() => {
        if (!pickerContainerRef.current || !containerRef?.current) return;

        const pickerContainer = pickerContainerRef.current;
        const uiGrid = containerRef.current;

        const adjustPickerPosition = () => {
            const uiGridRect = uiGrid.getBoundingClientRect();

            if (uiGrid.firstElementChild?.children) {
                const uiGridChildren = Array.from(
                    uiGrid.firstElementChild?.children
                );
                const nonAdSectionChildren = uiGridChildren.filter(
                    (child) => !child.classList.contains('ad-section')
                );

                const uiGridFirstElementRect =
                    nonAdSectionChildren?.[0]?.getBoundingClientRect();
                const uiGridLastElementRect =
                    nonAdSectionChildren?.[
                        nonAdSectionChildren.length - 1
                    ]?.getBoundingClientRect();
                const pickerRect = pickerContainer.getBoundingClientRect();
                const shouldStickToBottom =
                    window.innerHeight < uiGridRect.height;

                const topImageOffset = uiGridFirstElementRect
                    ? (uiGridFirstElementRect.height - pickerRect.height) / 2
                    : 0;
                const pickerFixedTopOffset =
                    (window.innerHeight - pickerRect.height) / 2;

                const uiGridRealHeight = Math.abs(
                    (uiGridFirstElementRect?.top ?? 0) -
                        (uiGridLastElementRect?.bottom ?? 0)
                );
                const topBoundary =
                    uiGridRect.top - pickerFixedTopOffset + topImageOffset;
                const bottomBoundary = uiGridLastElementRect
                    ? uiGridLastElementRect.bottom -
                      pickerRect.height -
                      pickerFixedTopOffset
                    : uiGridRect.bottom;

                if (
                    shouldStickToBottom &&
                    topBoundary <= 0 &&
                    bottomBoundary > 0
                ) {
                    pickerContainer.style.position = 'fixed';
                    pickerContainer.style.top = '50%';
                    pickerContainer.style.transform = 'translateY(-50%)';

                    // Auto-hide when expanded and scrolled past screen height (desktop only)
                    // Use the ref to access the scroll tracking variables
                    const currentScrollY = window.scrollY;
                    const scrollDelta = Math.abs(
                        currentScrollY - scrollVariablesRef.current.lastScrollY
                    );
                    scrollVariablesRef.current.scrollDistance += scrollDelta;
                    scrollVariablesRef.current.lastScrollY = currentScrollY;

                    // Auto-hide when scrolled past screen height - desktop only
                    if (
                        desktop &&
                        paintPickerActive &&
                        scrollVariablesRef.current.scrollDistance >
                            window.innerHeight &&
                        !isAnimating
                    ) {
                        closePaintPicker();
                        // Reset scroll distance for next time
                        scrollVariablesRef.current.scrollDistance = 0;
                        return;
                    }
                } else if (shouldStickToBottom && bottomBoundary <= 0) {
                    pickerContainer.style.position = 'absolute';
                    pickerContainer.style.top = `${uiGrid.offsetTop + uiGridRealHeight - pickerRect.height}px`;
                    pickerContainer.style.transform = 'none';
                } else {
                    pickerContainer.style.position = 'absolute';
                    pickerContainer.style.top = `${uiGrid.offsetTop + topImageOffset}px`;
                    pickerContainer.style.transform = 'none';
                }
            }
        };

        window.addEventListener('scroll', adjustPickerPosition);
        window.addEventListener('resize', adjustPickerPosition);
        adjustPickerPosition(); // Initial position setup

        return () => {
            window.removeEventListener('scroll', adjustPickerPosition);
            window.removeEventListener('resize', adjustPickerPosition);
        };
    }, [
        pickerContainerRef,
        containerRef,
        desktop,
        paintPickerActive,
        isAnimating,
        closePaintPicker,
    ]);

    const handleSavePaletteClick = (boardId: number, title?: string) => {
        if (!isSavingPalette) {
            paintSelection.length > 0
                ? savePaletteToDesignBoard(boardId, title)
                : setShowEmptyPaletteError(true);
        }
    };

    const itemIDs = paintSelection?.map((item) => item.productId) || [];

    return isEnabled ? (
        <motion.div
            id="resene-paint-picker"
            className={css(S.PaintPickerWrapper)}
            ref={desktop ? pickerContainerRef : drawerRef}
            initial={false}
            animate={paintPickerActive ? 'open' : 'closed'}
            variants={desktop ? S.wrapperAnimation : undefined}
            onAnimationStart={() => setIsAnimating(true)}
            onAnimationComplete={() => setIsAnimating(false)}
        >
            <motion.div
                initial={false}
                className={css(S.PaintPicker)}
                variants={
                    desktop ? S.containerAnimation : S.containerMobileAnimation
                }
                {...additionalContainerProps}
            >
                <motion.div variants={S.topAnimation} className={css(S.Top)}>
                    <a
                        className={css(S.Logo)}
                        target="_blank"
                        href={RESENE_PROFESSIONAL_LINK}
                        rel="noopener"
                        onClick={() =>
                            tracker.log('ReseneToolLogoClicked', {
                                url: new URL(window.location.href),
                            })
                        }
                    >
                        <Image
                            ref={reseneAdRef}
                            src={`${config.assetsBaseUrl}${RESENE_LOGO}`}
                            alt={'Resene'}
                            width={desktop ? 102 : 135}
                            height={desktop ? 32 : 42}
                            objectFit="contain"
                            layout={'intrinsic'}
                        />
                    </a>
                </motion.div>

                <div className={css(S.Main)}>
                    <PaintPalette
                        onOpen={openPaintPalette}
                        paintsContainerRef={paintsContainerRef}
                        isAnimating={isAnimating}
                    />
                </div>

                <motion.div
                    variants={S.bottomAnimation}
                    className={css(S.Bottom)}
                >
                    {paintSelection.length > 0 ? (
                        <div
                            className={css(S.SavePaletteButton)}
                            style={{ opacity: isSavingPalette ? 0.7 : 1 }}
                            onClick={() =>
                                handleFollow(
                                    itemIDs.map((id) => ({ id })),
                                    'ProductPin'
                                )
                            }
                        >
                            {isSavingPalette ? 'Saving...' : 'Save Palette'}
                        </div>
                    ) : (
                        <div
                            className={css(S.SavePaletteButton)}
                            onClick={() => handleSavePaletteClick(0)}
                        >
                            Save Palette
                        </div>
                    )}

                    {desktop && (
                        <div
                            className={css(S.CloseButton)}
                            onClick={closePaintPalette}
                        >
                            <AngleLeft className={css(S.CloseButtonIcon)} />
                            <span>Hide</span>
                        </div>
                    )}
                </motion.div>
            </motion.div>

            {showEmptyPaletteError && (
                <div className={css(S.SavePaletteEmptyModal)}>
                    <PaintPickerSavePaletteEmptyModal
                        onDismiss={() => setShowEmptyPaletteError(false)}
                    />
                </div>
            )}
        </motion.div>
    ) : null;
};

const usePaintDrawerMobileDrag = (
    onClose: () => void,
    closeThreshold = 50,
    dragHandleHeight = 50
) => {
    const [touchStartY, setTouchStartY] = useState<number | null>(null);
    const [drawerPosition, setDrawerPosition] = useState(0);
    const [closing, setClosing] = useState(false);
    const drawerRef = useRef<HTMLDivElement>(null);

    const handleTouchStart = useCallback(
        (e: React.TouchEvent) => {
            if (e.touches[0] && drawerRef.current) {
                const touch = e.touches[0];
                const drawerRect = drawerRef.current.getBoundingClientRect();
                const touchY = touch.clientY - drawerRect.top;

                // Check if touch is within the drag handle area
                if (touchY <= dragHandleHeight) {
                    setTouchStartY(touch.clientY);
                }
            }
        },
        [dragHandleHeight]
    );

    const handleTouchMove = useCallback(
        (e: React.TouchEvent) => {
            if (touchStartY !== null && e.touches[0]) {
                const touchCurrentY = e.touches[0].clientY;
                const dragDistance = touchCurrentY - touchStartY;

                // Update drawer position
                setDrawerPosition(Math.max(0, dragDistance));
                setClosing(dragDistance > closeThreshold);
            }
        },
        [touchStartY, closeThreshold]
    );

    const handleTouchEnd = useCallback(() => {
        if (touchStartY !== null) {
            setTouchStartY(null);

            if (closing) {
                onClose();
                setTimeout(() => setDrawerPosition(0), 200);
            } else {
                setDrawerPosition(0);
            }
        }
    }, [closing, touchStartY, onClose]);

    // Prevent body scrolling when drawer is being dragged
    useEffect(() => {
        const preventDefault = (e: TouchEvent) => {
            if (touchStartY !== null) {
                e.preventDefault();
            }
        };

        document.body.addEventListener('touchmove', preventDefault, {
            passive: false,
        });

        return () => {
            document.body.removeEventListener('touchmove', preventDefault);
        };
    }, [touchStartY]);

    return {
        drawerRef,
        drawerPosition,
        dragHandlers: {
            onTouchStart: handleTouchStart,
            onTouchMove: handleTouchMove,
            onTouchEnd: handleTouchEnd,
        },
    };
};
